// 原価再計算まわりの計算・反映・表示更新をまとめる。
export function createGenkaRecalcService(deps) {
    const {
        PANEL_TYPE,
        entrydataRef,
        totalgriddataRefs,
        entryLoadingRef,
        genkaPatternNo,
        genkaPattern,
        isEmpty,
        DicVal,
        DeepCopy,
        GridData,
        SetGridData,
        applyGridRowsNow,
        applyTotalRowsNow,
        ensureTotalDataRef,
        rerender,
        roundByDecimal,
        normalizeTotalTypeForColumns,
        normalizeGenkaSummaryRows,
        defaultGenkaSummaryRows,
        buildGenkaSummaryRowsFromFlatData,
        buildGenkaRowsForDisplay,
        normalizeGenkaRowsWithSkeleton,
        buildGenkaSkeletonRows,
        blankDefaultZeroGenkaRows,
        blankDefaultZeroGenkaSummaryRows,
        applyGenkaSummaryRowsToEntryData,
        deriveGenkaPatternFromEntry,
        toGenkaRowKey,
        resolveGenkaSummaryItem,
        resolveGenkaSummaryKeyId,
        emptyGenkaRow,
        emptyGenkaSummaryRow,
        genkaRowLabelByKey,
        genkaRowKeyOrder,
        genkaSummaryOrder,
        genkaSummaryColumnCandidates,
        genkaColumnMapByColumnIdUpper,
        shouldForceGenkaCurrencyJpy,
        applyForcedGenkaJpyToSummaryRows,
        hasActualGenkaSummaryEntryValueForField,
        isGenkaKeepZeroSummaryField,
        normalizeGenkaRowsByLayoutPrecision,
        applyGenkaCalcDisplayRounding,
        findColumnInfoByCandidates,
        isMeaningfulGenkaValue,
        isGenkaTouchedColumnId,
        isGenkaKouseiEnabledRow,
        isGenkaGridPanelInfo,
        resolveTempGenkaPatternNo
    } = deps;

    // 原価（セル式優先）で使うスロット定義。
    const genkaCalcSlots = ["muryo", "muryo_f", "yusho", "yusho_f", "kojo", "kojo_f", "syokei", "syokei_f", "kousei"];
    const genkaSourceSlots = ["muryo", "muryo_f", "yusho", "yusho_f", "kojo", "kojo_f"];
    const genkaCalculatedRowKeys = ["MAT_TOTAL", "SUB_TOTAL", "EST_COST"];
    const genkaSummarySourceItems = ["SSDGM_BOUEKIJYOKEN", "SSDGM_JYODAI"];

    // 原価明細の行キーと表示名をパターン前提で正規化する。
    function normalizeGenkaRowsByPattern(rows) {
        if (!Array.isArray(rows) || rows.length === 0) return rows;
        return rows.map((r) => {
            const rowKey = toGenkaRowKey(DicVal(r, "row_key", "") || DicVal(r, "item_name", ""));
            return isEmpty(rowKey) ? r : {
                ...r,
                row_key: rowKey,
                item_name: String(genkaRowLabelByKey[rowKey] ?? DicVal(r, "item_name", ""))
            };
        });
    }

    function toGenkaCalcNumber(value) {
        if (isEmpty(value)) return 0;
        const raw = String(value ?? "")
            .replace(/[,\s　]/g, "")
            .replace(/―/g, "-")
            .trim();
        if (raw === "" || raw === "-" || raw === "―") return 0;
        const n = Number(raw);
        return Number.isFinite(n) ? n : 0;
    }

    function roundGenkaCalc(value) {
        const n = Number(value);
        if (!Number.isFinite(n)) return 0;
        return Math.round((n + Number.EPSILON) * 1000000) / 1000000;
    }

    function calcRate(value) {
        return toGenkaCalcNumber(value) / 100;
    }

    function isGenkaKeepZeroColumnId(columnId) {
        const cid = String(columnId ?? "").trim().toUpperCase();
        if (cid === "") return false;
        const conf = genkaSummaryColumnCandidates["SSDGM_BOUEKIJYOKEN"];
        const ids = [...(DicVal(conf, "value", []) || [])]
            .map(v => String(v ?? "").trim().toUpperCase());
        return ids.includes(cid);
    }

    function hasActualEntryValueByColumnId(entryObj, columnId) {
        if (isEmpty(entryObj) || isEmpty(columnId)) return false;
        const target = String(columnId ?? "").trim().toUpperCase();
        const keys = Object.keys(entryObj);
        const key = keys.find((k) => String(k ?? "").trim().toUpperCase() === target);
        if (isEmpty(key)) return false;
        const raw = String(entryObj[key] ?? "").trim();
        return isMeaningfulGenkaValue(raw, isGenkaTouchedColumnId(target) || isGenkaKeepZeroColumnId(target));
    }

    function hasActualGenkaEntryValues(entryObj) {
        if (isEmpty(entryObj)) return false;
        const leftColumnIds = Object.keys(genkaColumnMapByColumnIdUpper || {});
        if (leftColumnIds.some((cid) => hasActualEntryValueByColumnId(entryObj, cid))) return true;
        return genkaSummarySourceItems.some((item) => {
            const conf = genkaSummaryColumnCandidates[item];
            if (isEmpty(conf)) return false;
            const ids = [...(conf.value || []), ...(conf.value2 || [])];
            return ids.some((cid) => hasActualEntryValueByColumnId(entryObj, cid));
        });
    }

    function hasActualGenkaEntryValueForCell(rowKey, slot) {
        const targetRow = toGenkaRowKey(rowKey);
        const targetSlot = String(slot ?? "");
        return Object.keys(genkaColumnMapByColumnIdUpper || {}).some((cid) => {
            const map = genkaColumnMapByColumnIdUpper[cid];
            if (toGenkaRowKey(DicVal(map, "row", "")) !== targetRow) return false;
            const slots = Array.isArray(DicVal(map, "cols", null)) ? map.cols : [];
            if (!slots.includes(targetSlot)) return false;
            return hasActualEntryValueByColumnId(entrydataRef.current, cid);
        });
    }

    function hasActualGenkaGridValues(rows) {
        if (!Array.isArray(rows) || rows.length === 0) return false;
        return rows.some((row) => {
            const rowKey = toGenkaRowKey(DicVal(row, "row_key", "") || DicVal(row, "item_name", ""));
            if (isEmpty(rowKey) || genkaCalculatedRowKeys.includes(rowKey)) return false;
            return genkaSourceSlots.some((slot) => {
                const raw = String(DicVal(row, slot, "") ?? "").trim();
                return isMeaningfulGenkaValue(raw, hasActualGenkaEntryValueForCell(rowKey, slot));
            });
        });
    }

    function hasActualGenkaSummaryValues(source) {
        if (Array.isArray(source)) {
            return source.some((row) => {
                const item = resolveGenkaSummaryItem(DicVal(row, "total_key", "") || DicVal(row, "total_item", ""));
                if (!genkaSummarySourceItems.includes(item)) return false;
                const raw1 = String(DicVal(row, "total_value", "") ?? "").trim();
                const raw2 = String(DicVal(row, "total_value2", "") ?? "").trim();
                const keep1 = isMeaningfulGenkaValue(raw1, hasActualGenkaSummaryEntryValueForField(item, false) || isGenkaKeepZeroSummaryField(item, false));
                const keep2 = isMeaningfulGenkaValue(raw2, hasActualGenkaSummaryEntryValueForField(item, true) || isGenkaKeepZeroSummaryField(item, true));
                return keep1 || keep2;
            });
        }
        if (isEmpty(source)) return false;
        return genkaSummarySourceItems.some((item) => {
            const conf = genkaSummaryColumnCandidates[item];
            if (isEmpty(conf)) return false;
            const ids = [...(conf.value || []), ...(conf.value2 || [])];
            return ids.some((cid) => hasActualEntryValueByColumnId(source, cid));
        });
    }

    function hasActualGenkaSummaryEntryValue(item) {
        const conf = genkaSummaryColumnCandidates[item];
        if (isEmpty(conf)) return false;
        const ids = [...(conf.value || []), ...(conf.value2 || [])];
        return ids.some((cid) => hasActualEntryValueByColumnId(entrydataRef.current, cid));
    }

    function currentGenkaSummaryRowsForActualCheck() {
        const keys = Object.keys(totalgriddataRefs.current || {});
        for (let i = 0; i < keys.length; i++) {
            const key = String(keys[i] ?? "");
            if (!key.startsWith("19_") && !key.startsWith("genka_")) continue;
            const rows = DicVal(DicVal(totalgriddataRefs.current, key, null), "current", null);
            if (Array.isArray(rows) && rows.length !== 0) return rows;
        }
        return [];
    }

    function hasActualGenkaRecalcSource(baseRows) {
        return hasActualGenkaEntryValues(entrydataRef.current)
            || hasActualGenkaGridValues(baseRows)
            || hasActualGenkaSummaryValues(currentGenkaSummaryRowsForActualCheck());
    }

    function getGenkaRowMap(rows) {
        const map = {};
        genkaRowKeyOrder.forEach((k) => { map[k] = emptyGenkaRow(k, genkaRowLabelByKey[k], "0"); });
        if (!Array.isArray(rows)) return map;
        rows.forEach((r, idx) => {
            const rowKey = toGenkaRowKey(DicVal(r, "row_key", "") || DicVal(r, "item_name", ""));
            if (isEmpty(rowKey) || isEmpty(map[rowKey])) return;
            const base = {
                ...map[rowKey],
                ...r,
                rowid: String(DicVal(r, "rowid", idx)),
                row_key: rowKey,
                item_name: String(genkaRowLabelByKey[rowKey] ?? DicVal(r, "item_name", ""))
            };
            genkaCalcSlots.forEach((slot) => {
                const raw = DicVal(r, slot, "");
                base[slot] = isEmpty(raw) ? "" : String(raw);
            });
            map[rowKey] = base;
        });
        return map;
    }

    function genkaMainSlotByPatternNo(no) {
        if ([2, 6, 7].includes(no)) return "yusho";
        return "muryo";
    }

    function genkaMainFSlotByPatternNo(no) {
        if (no === 7) return "yusho_f";
        return "muryo_f";
    }

    function genkaExpenseSlotByPatternNo(no) {
        if ([6, 7].includes(no)) return "yusho";
        return "muryo";
    }

    function genkaParsePatternNoForCalc() {
        const forced = resolveTempGenkaPatternNo();
        if (forced !== 0) return forced;
        const stateNo = Number(genkaPatternNo);
        if (Number.isFinite(stateNo) && stateNo !== 0) return stateNo;
        const derived = Number(deriveGenkaPatternFromEntry(entrydataRef.current));
        if (Number.isFinite(derived)) return derived;
        const m = String(genkaPattern ?? "").match(/^\s*(\d+)/);
        if (!isEmpty(m)) return Number(m[1]);
        return 0;
    }

    function getGenkaSummaryBaseRows(options = {}) {
        const keys = Object.keys(totalgriddataRefs.current || {});
        const hitKey = keys.find((k) => {
            const s = String(k ?? "");
            return s.startsWith("19_") || s.startsWith("genka_");
        });
        if (!isEmpty(hitKey)) {
            const cur = DicVal(totalgriddataRefs.current, hitKey, null);
            if (!isEmpty(cur) && Array.isArray(cur.current) && cur.current.length !== 0) {
                return applyForcedGenkaJpyToSummaryRows(normalizeGenkaSummaryRows(cur.current));
            }
        }
        const rebuilt = buildGenkaSummaryRowsFromFlatData(entrydataRef.current);
        if (Array.isArray(rebuilt) && rebuilt.length !== 0) return applyForcedGenkaJpyToSummaryRows(normalizeGenkaSummaryRows(rebuilt));
        return applyForcedGenkaJpyToSummaryRows(defaultGenkaSummaryRows());
    }

    function setGenkaSummaryCell(summaryMap, item, field, value) {
        if (isEmpty(summaryMap) || isEmpty(summaryMap[item])) return;
        summaryMap[item][field] = String(value ?? "");
    }

    function genkaRateForLeftCalc(options = {}) {
        if (shouldForceGenkaCurrencyJpy(entrydataRef.current)) return 1;
        const rows = normalizeGenkaSummaryRows(getGenkaSummaryBaseRows(options));
        const rateRow = rows.find((r) => resolveGenkaSummaryItem(DicVal(r, "total_item", "") || DicVal(r, "total_key", "")) === "SSDGM_RATE");
        const raw = String(DicVal(rateRow, "total_value", "") ?? "").trim();
        if (raw === "") return 0;
        const rate = toGenkaCalcNumber(raw);
        return Number.isFinite(rate) ? rate : 0;
    }

    function applyGenkaForeignCurrencyToYen(rows, patternNo, rate) {
        if (![5, 7, 9].includes(Number(patternNo)) || rate === 0) return;
        const moneyRows = ["MAT_AMOUNT", "SUB_AMOUNT", "NAME_AMOUNT", "DELIVERY_MAT", "PROCESS", "SECONDARY", "PRODUCT"];
        const rateRows = ["MAT_LOSS", "SUB_LOSS", "ETC_LOSS", "MARGIN"];
        const pairs = Number(patternNo) === 7
            ? [{ from: "yusho_f", to: "yusho" }, { from: "kojo_f", to: "kojo" }]
            : [{ from: "kojo_f", to: "kojo" }];

        // 率項目は外貨列の値をそのまま国内列へ引き継ぐ。
        rateRows.forEach((rowKey) => {
            if (isEmpty(rows[rowKey])) return;
            pairs.forEach((pair) => {
                const raw = String(DicVal(rows[rowKey], pair.from, "") ?? "").trim();
                if (raw === "") return;
                rows[rowKey][pair.to] = raw;
            });
        });

        // 金額項目はレート換算した値を国内列へ反映する。
        moneyRows.forEach((rowKey) => {
            if (isEmpty(rows[rowKey])) return;
            pairs.forEach((pair) => {
                const raw = String(DicVal(rows[rowKey], pair.from, "") ?? "").trim();
                if (raw === "") return;
                rows[rowKey][pair.to] = String(roundGenkaCalc(toGenkaCalcNumber(raw) * rate));
            });
        });
    }

    // 原価左グリッドの派生行を再計算する。
    // 入力セルは保持し、計算行のみ上書きする。
    // パターン1はExcelセル式に不足があるため、工場手配側の素材金額計/附属金額計を見積原価へ含める業務補正を行う。
    // 金額計・小計は表示上丸めるが、計算順序は既存仕様を維持する。
    function calcGenkaRowsByCellFormula(inputRows, options = {}) {
        const patternNo = genkaParsePatternNoForCalc();
        const main = genkaMainSlotByPatternNo(patternNo);
        const mainF = genkaMainFSlotByPatternNo(patternNo);
        const expenseSlot = genkaExpenseSlotByPatternNo(patternNo);
        const rows = getGenkaRowMap(inputRows);
        const n = (rowKey, slot) => toGenkaCalcNumber(DicVal(rows[rowKey], slot, 0));
        const has = (rowKey, slot) => String(DicVal(rows[rowKey], slot, "") ?? "").trim() !== "";
        const w = (rowKey, slot, value) => { rows[rowKey][slot] = String(roundGenkaCalc(value)); };
        const hasAny = (pairs) => pairs.some((p) => has(p[0], p[1]));
        const hasMainEstSource = (slot) => {
            const pairs = [
                ["MAT_TOTAL", slot],
                ["SUB_TOTAL", slot],
                ["SECONDARY", slot],
                ["ADJUST", slot]
            ];
            if (patternNo !== 1) {
                pairs.push(
                    ["EXPENSE_OUT", expenseSlot],
                    ["EXPENSE_IN", expenseSlot],
                    ["TARIFF", expenseSlot],
                    ["ZAN8", expenseSlot],
                    ["POST_SUB", expenseSlot],
                    ["DOMESTIC_WORK", expenseSlot]
                );
            }
            return hasAny(pairs);
        };
        const hasKojoEstSource = (slot) => {
            if (patternNo === 1) return hasAny([
                ["MAT_TOTAL", slot],
                ["SUB_TOTAL", slot],
                ["PROCESS", slot],
                ["SECONDARY", slot],
                ["MARGIN", slot],
                ["ADJUST", slot]
            ]);
            if (patternNo === 2) return hasAny([
                ["SUB_TOTAL", slot],
                ["PROCESS", slot],
                ["SECONDARY", slot],
                ["MARGIN", slot],
                ["ADJUST", slot]
            ]);
            if (patternNo === 3) return hasAny([["PRODUCT", slot], ["MARGIN", slot], ["ADJUST", slot]]);
            return hasAny([["PROCESS", slot], ["MARGIN", slot], ["ADJUST", slot]]);
        };
        const originalEstCostValues = {
            main: String(DicVal(rows.EST_COST, main, "") ?? "").trim(),
            kojo: String(DicVal(rows.EST_COST, "kojo", "") ?? "").trim(),
            kojo_f: String(DicVal(rows.EST_COST, "kojo_f", "") ?? "").trim(),
            yusho_f: String(DicVal(rows.EST_COST, "yusho_f", "") ?? "").trim()
        };

        applyGenkaForeignCurrencyToYen(rows, patternNo, genkaRateForLeftCalc(options));

        const rowKeysForLoss = ["muryo", "muryo_f", "yusho", "yusho_f", "kojo", "kojo_f"];
        rowKeysForLoss.forEach((slot) => {
            w("MAT_TOTAL", slot, n("MAT_AMOUNT", slot) * (1 + calcRate(n("MAT_LOSS", slot))));
            if (!has("MAT_AMOUNT", slot) && !has("MAT_LOSS", slot)) rows.MAT_TOTAL[slot] = "";
        });

        // 業務上、納品資材は附属金額計に含める。
        ["muryo", "muryo_f", "yusho", "yusho_f"].forEach((slot) => {
            const sub = n("SUB_AMOUNT", slot) * (1 + calcRate(n("SUB_LOSS", slot)));
            const name = n("NAME_AMOUNT", slot) * (1 + calcRate(n("ETC_LOSS", slot)));
            w("SUB_TOTAL", slot, sub + name + n("DELIVERY_MAT", slot));
            if (!has("SUB_AMOUNT", slot) && !has("SUB_LOSS", slot) && !has("NAME_AMOUNT", slot) && !has("ETC_LOSS", slot) && !has("DELIVERY_MAT", slot)) rows.SUB_TOTAL[slot] = "";
        });

        // 工場手配/工場手配(外)ともにロス率（附属/その他附属）を加味して合算する。
        const subKojo = n("SUB_AMOUNT", "kojo") * (1 + calcRate(n("SUB_LOSS", "kojo")));
        const nameKojo = n("NAME_AMOUNT", "kojo") * (1 + calcRate(n("ETC_LOSS", "kojo")));
        w("SUB_TOTAL", "kojo", subKojo + nameKojo + n("DELIVERY_MAT", "kojo"));
        if (!has("SUB_AMOUNT", "kojo") && !has("SUB_LOSS", "kojo") && !has("NAME_AMOUNT", "kojo") && !has("ETC_LOSS", "kojo") && !has("DELIVERY_MAT", "kojo")) rows.SUB_TOTAL.kojo = "";
        const subKojoF = n("SUB_AMOUNT", "kojo_f") * (1 + calcRate(n("SUB_LOSS", "kojo_f")));
        const nameKojoF = n("NAME_AMOUNT", "kojo_f") * (1 + calcRate(n("ETC_LOSS", "kojo_f")));
        w("SUB_TOTAL", "kojo_f", subKojoF + nameKojoF + n("DELIVERY_MAT", "kojo_f"));
        if (!has("SUB_AMOUNT", "kojo_f") && !has("SUB_LOSS", "kojo_f") && !has("NAME_AMOUNT", "kojo_f") && !has("ETC_LOSS", "kojo_f") && !has("DELIVERY_MAT", "kojo_f")) rows.SUB_TOTAL.kojo_f = "";

        const ratioRows = new Set(["MAT_LOSS", "SUB_LOSS", "ETC_LOSS", "MARGIN"]);
        genkaRowKeyOrder.forEach((rowKey) => {
            if (ratioRows.has(rowKey)) {
                rows[rowKey].syokei = "";
                return;
            }
            w(rowKey, "syokei", n(rowKey, main) + n(rowKey, "kojo"));
            if (!has(rowKey, main) && !has(rowKey, "kojo") && toGenkaCalcNumber(DicVal(rows[rowKey], "syokei", 0)) === 0) rows[rowKey].syokei = "";
        });
        genkaRowKeyOrder.forEach((rowKey) => {
            if (ratioRows.has(rowKey)) {
                rows[rowKey].syokei_f = "";
                return;
            }
            if (patternNo === 5 || patternNo === 9 || patternNo === 8) {
                rows[rowKey].syokei_f = "";
                return;
            }
            w(rowKey, "syokei_f", n(rowKey, mainF) + n(rowKey, "kojo_f"));
            if (!has(rowKey, mainF) && !has(rowKey, "kojo_f") && toGenkaCalcNumber(DicVal(rows[rowKey], "syokei_f", 0)) === 0) rows[rowKey].syokei_f = "";
        });

        const expense = n("EXPENSE_OUT", expenseSlot)
            + n("EXPENSE_IN", expenseSlot)
            + n("TARIFF", expenseSlot)
            - n("ZAN8", expenseSlot)
            + n("POST_SUB", expenseSlot)
            + n("DOMESTIC_WORK", expenseSlot);
        const mainEst = (patternNo === 1)
            ? (
                n("MAT_TOTAL", main)
                + n("SUB_TOTAL", main)
                + n("SECONDARY", main)
                + n("ADJUST", main)
            )
            : (
                n("MAT_TOTAL", main)
                + n("SUB_TOTAL", main)
                + n("SECONDARY", main)
                + n("ADJUST", main)
                + expense
            );
        w("EST_COST", main, mainEst);
        if (!hasMainEstSource(main)) rows.EST_COST[main] = hasActualGenkaEntryValueForCell("EST_COST", main) ? originalEstCostValues.main : "";

        let kojoEst = 0;
        if (patternNo === 1) {
            // 業務補正: Excelセル式(AL815)は素材金額計/二次加工賃の寄与が不足するため、
            // パターン1は「素材金額計+附属金額計+加工賃+二次加工賃」にマージンを掛け、最後に調整を加算する。
            kojoEst = (
                n("MAT_TOTAL", "kojo")
                + n("SUB_TOTAL", "kojo")
                + n("PROCESS", "kojo")
                + n("SECONDARY", "kojo")
            ) * (1 + calcRate(n("MARGIN", "kojo"))) + n("ADJUST", "kojo");
        } else if (patternNo === 2) {
            kojoEst = (n("SUB_TOTAL", "kojo") + n("PROCESS", "kojo") + n("SECONDARY", "kojo")) * (1 + calcRate(n("MARGIN", "kojo"))) + n("ADJUST", "kojo");
        } else if (patternNo === 3) {
            kojoEst = n("PRODUCT", "kojo") * (1 + calcRate(n("MARGIN", "kojo"))) + n("ADJUST", "kojo");
        } else {
            kojoEst = n("PROCESS", "kojo") * (1 + calcRate(n("MARGIN", "kojo"))) + n("ADJUST", "kojo");
        }
        w("EST_COST", "kojo", kojoEst);
        if (!hasKojoEstSource("kojo")) rows.EST_COST.kojo = hasActualGenkaEntryValueForCell("EST_COST", "kojo") ? originalEstCostValues.kojo : "";

        let kojoFEst = 0;
        if ([5, 7, 9].includes(patternNo)) {
            kojoFEst = n("PROCESS", "kojo_f") * (1 + calcRate(n("MARGIN", "kojo_f"))) + n("ADJUST", "kojo_f");
            w("EST_COST", "kojo_f", kojoFEst);
            if (!hasKojoEstSource("kojo_f")) rows.EST_COST.kojo_f = hasActualGenkaEntryValueForCell("EST_COST", "kojo_f") ? originalEstCostValues.kojo_f : "";
        } else {
            rows.EST_COST.kojo_f = "";
        }

        let estF = 0;
        if (patternNo === 7) {
            const extMain = n("MAT_TOTAL", "yusho_f") + n("SUB_TOTAL", "yusho_f") + n("SECONDARY", "yusho_f");
            w("EST_COST", "yusho_f", extMain);
            if (!hasAny([["MAT_TOTAL", "yusho_f"], ["SUB_TOTAL", "yusho_f"], ["SECONDARY", "yusho_f"]])) rows.EST_COST.yusho_f = hasActualGenkaEntryValueForCell("EST_COST", "yusho_f") ? originalEstCostValues.yusho_f : "";
            estF = extMain + kojoFEst;
            rows.EST_COST.syokei_f = (has("EST_COST", "yusho_f") || has("EST_COST", "kojo_f")) ? String(roundGenkaCalc(estF)) : "";
        } else if ([5, 9].includes(patternNo)) {
            rows.EST_COST.syokei_f = "";
        } else {
            rows.EST_COST.syokei_f = "";
        }
        rows.EST_COST.syokei = (has("EST_COST", main) || has("EST_COST", "kojo")) ? String(roundGenkaCalc(n("EST_COST", main) + n("EST_COST", "kojo"))) : "";

        const estSyokei = toGenkaCalcNumber(rows.EST_COST.syokei);
        genkaRowKeyOrder.forEach((rowKey) => {
            if (!isGenkaKouseiEnabledRow(rowKey, patternNo)) {
                rows[rowKey].kousei = "";
                return;
            }
            const syokeiRaw = String(DicVal(rows[rowKey], "syokei", "") ?? "").trim();
            if (syokeiRaw === "") {
                rows[rowKey].kousei = "";
                return;
            }
            const ratio = estSyokei === 0 ? 0 : (toGenkaCalcNumber(rows[rowKey].syokei) / estSyokei);
            rows[rowKey].kousei = String(Math.round(ratio * 100));
        });

        const normalizedRows = genkaRowKeyOrder.map((k, idx) => ({
            ...rows[k],
            rowid: String(DicVal(rows[k], "rowid", idx)),
            row_key: k,
            item_name: String(genkaRowLabelByKey[k] ?? DicVal(rows[k], "item_name", ""))
        }));

        return {
            rows: applyGenkaCalcDisplayRounding(normalizeGenkaRowsByLayoutPrecision(normalizedRows)),
            patternNo: patternNo,
            expenseSlot: expenseSlot
        };
    }

    function formatGenkaSummaryNumberByItem(item, isValue2, value) {
        const cands = DicVal(genkaSummaryColumnCandidates, item, null);
        if (isEmpty(cands)) return String(roundGenkaCalc(value));
        const list = isValue2 ? (cands.value2 || []) : (cands.value || []);
        const cinfo = findColumnInfoByCandidates(list, [PANEL_TYPE.GENKA_SUMMARY, "127"]);
        if (isEmpty(cinfo) || String(cinfo.datatype ?? "") !== "1") return String(roundGenkaCalc(value));
        return roundByDecimal(roundGenkaCalc(value), DicVal(cinfo, "decimalplace", 0));
    }

    // 原価右「合計」を再計算する。
    // 仕入原価・設定率・発注単価は左原価計算結果を元に作る。
    // FOB小計/諸掛計/発注単価(外)は、対象パターン・元入力の有無に応じて空欄/計算値を出し分ける。
    function calcGenkaSummaryRowsByCellFormula(calcRows, patternNo, expenseSlot, options = {}) {
        const baseRows = getGenkaSummaryBaseRows(options);
        const map = {};

        // total_item / total_key がどちらで来ても、内部計算キーはColumn IDへ正規化する。
        baseRows.forEach((r) => {
            const key = resolveGenkaSummaryItem(DicVal(r, "total_key", "") || DicVal(r, "total_item", ""));
            if (key === "") return;
            map[key] = {
                ...r,
                total_item: key,
                total_key: resolveGenkaSummaryKeyId(key)
            };
        });

        // 返却元が空でも、計算対象行が欠けないように固定行を補完。
        genkaSummaryOrder.forEach((item, idx) => {
            if (!isEmpty(map[item])) return;
            map[item] = emptyGenkaSummaryRow(item, idx);
        });

        const rowMap = getGenkaRowMap(calcRows);
        const n = (rowKey, slot) => toGenkaCalcNumber(DicVal(rowMap[rowKey], slot, 0));
        const readSummary = (item, field) => toGenkaCalcNumber(DicVal(DicVal(map, item, {}), field, 0));
        const hasSummaryValue = (item, field) => {
            const raw = String(DicVal(DicVal(map, item, {}), field, "") ?? "").trim();
            return raw !== "";
        };
        const hasGenkaCellValue = (rowKey, slot) => {
            const raw = String(DicVal(DicVal(rowMap, rowKey, {}), slot, "") ?? "").trim();
            return raw !== "";
        };
        const hasAnyGenkaCellValue = (pairs) => pairs.some((p) => hasGenkaCellValue(p[0], p[1]));
        const currentSummaryValue = (item) => String(DicVal(DicVal(map, item, {}), "total_value", "") ?? "").trim();

        const fobf = readSummary("SSDGM_BOUEKIJYOKEN", "total_value2");
        const rate = readSummary("SSDGM_RATE", "total_value");
        const fob = fobf * rate;
        const syokakari = n("EXPENSE_OUT", expenseSlot) + n("EXPENSE_IN", expenseSlot) + n("TARIFF", expenseSlot) - n("ZAN8", expenseSlot);
        const shiire = n("EST_COST", "syokei");
        const jyodai = readSummary("SSDGM_JYODAI", "total_value");
        const settei = shiire === 0 ? 0 : (jyodai / shiire);

        let hachu = 0;
        let hachuF = 0;
        if (patternNo === 1) {
            hachu = n("EST_COST", "kojo");
        } else if (patternNo === 2) {
            hachu = shiire;
        } else if (patternNo === 4) {
            // パターン4は仕様書注記「発注単価＝加工賃の小計」を優先する。
            hachu = n("PROCESS", "syokei");
        } else if (patternNo === 3 || patternNo === 5 || patternNo === 8 || patternNo === 9) {
            hachu = n("EST_COST", "kojo");
        } else if (patternNo === 6 || patternNo === 7) {
            const margin = calcRate(n("MARGIN", "kojo"));
            const base = n("MAT_TOTAL", "syokei") + n("SUB_TOTAL", "syokei") + n("PROCESS", "syokei") + n("SECONDARY", "syokei");
            hachu = base * (1 + margin);
        }

        if (patternNo === 5 || patternNo === 9) {
            hachuF = n("EST_COST", "kojo_f");
        } else if (patternNo === 7) {
            const marginF = calcRate(n("MARGIN", "kojo_f"));
            const baseF = n("MAT_TOTAL", "syokei_f") + n("SUB_TOTAL", "syokei_f") + n("PROCESS", "syokei_f") + n("SECONDARY", "syokei_f");
            hachuF = baseF * (1 + marginF);
        }

        const hasShiireSource = hasGenkaCellValue("EST_COST", "syokei");
        const hasSetteiSource = hasShiireSource && hasSummaryValue("SSDGM_JYODAI", "total_value");
        const hasHachuSource = (() => {
            if (patternNo === 1) return hasGenkaCellValue("EST_COST", "kojo");
            if (patternNo === 2) return hasShiireSource;
            if (patternNo === 4) return hasGenkaCellValue("PROCESS", "syokei");
            if (patternNo === 3 || patternNo === 5 || patternNo === 8 || patternNo === 9) return hasGenkaCellValue("EST_COST", "kojo");
            if (patternNo === 6 || patternNo === 7) {
                return hasAnyGenkaCellValue([
                    ["MAT_TOTAL", "syokei"],
                    ["SUB_TOTAL", "syokei"],
                    ["PROCESS", "syokei"],
                    ["SECONDARY", "syokei"],
                    ["MARGIN", "kojo"]
                ]);
            }
            return false;
        })();
        const hasHachuFSource = (() => {
            if (patternNo === 5 || patternNo === 9) return hasGenkaCellValue("EST_COST", "kojo_f");
            if (patternNo === 7) {
                return hasAnyGenkaCellValue([
                    ["MAT_TOTAL", "syokei_f"],
                    ["SUB_TOTAL", "syokei_f"],
                    ["PROCESS", "syokei_f"],
                    ["SECONDARY", "syokei_f"],
                    ["MARGIN", "kojo_f"]
                ]);
            }
            return false;
        })();

        const hasFobInput = hasSummaryValue("SSDGM_BOUEKIJYOKEN", "total_value2") || hasSummaryValue("SSDGM_RATE", "total_value");
        if (hasFobInput) {
            setGenkaSummaryCell(map, "SSDGM_FOBKIN", "total_value", formatGenkaSummaryNumberByItem("SSDGM_FOBKIN", false, fob));
        } else {
            setGenkaSummaryCell(map, "SSDGM_FOBKIN", "total_value", "");
        }

        const hasSyokakariInput =
            hasGenkaCellValue("EXPENSE_OUT", expenseSlot)
            || hasGenkaCellValue("EXPENSE_IN", expenseSlot)
            || hasGenkaCellValue("TARIFF", expenseSlot)
            || hasGenkaCellValue("ZAN8", expenseSlot);
        if (hasSyokakariInput) {
            setGenkaSummaryCell(map, "SSDGM_SYOKAKARITAKA", "total_value", formatGenkaSummaryNumberByItem("SSDGM_SYOKAKARITAKA", false, syokakari));
        } else {
            setGenkaSummaryCell(map, "SSDGM_SYOKAKARITAKA", "total_value", "");
        }

        setGenkaSummaryCell(map, "SSDGM_SEIHINGENKA", "total_value", hasShiireSource ? formatGenkaSummaryNumberByItem("SSDGM_SEIHINGENKA", false, shiire) : (hasActualGenkaSummaryEntryValue("SSDGM_SEIHINGENKA") ? currentSummaryValue("SSDGM_SEIHINGENKA") : ""));
        setGenkaSummaryCell(map, "SSDGM_JYODAIRITU", "total_value", hasSetteiSource ? formatGenkaSummaryNumberByItem("SSDGM_JYODAIRITU", false, settei) : (hasActualGenkaSummaryEntryValue("SSDGM_JYODAIRITU") ? currentSummaryValue("SSDGM_JYODAIRITU") : ""));
        setGenkaSummaryCell(map, "SSDGM_HACHUTANKA", "total_value", hasHachuSource ? formatGenkaSummaryNumberByItem("SSDGM_HACHUTANKA", false, hachu) : (hasActualGenkaSummaryEntryValue("SSDGM_HACHUTANKA") ? currentSummaryValue("SSDGM_HACHUTANKA") : ""));
        if ((patternNo === 5 || patternNo === 7 || patternNo === 9) && (hasHachuFSource || hasActualGenkaSummaryEntryValue("SSDGM_HACHUTANKAF"))) {
            setGenkaSummaryCell(map, "SSDGM_HACHUTANKAF", "total_value", hasHachuFSource ? formatGenkaSummaryNumberByItem("SSDGM_HACHUTANKAF", false, hachuF) : currentSummaryValue("SSDGM_HACHUTANKAF"));
        } else {
            setGenkaSummaryCell(map, "SSDGM_HACHUTANKAF", "total_value", "");
        }

        // 円固定レイアウトでは通貨/レートも強制補正する。
        if (shouldForceGenkaCurrencyJpy(entrydataRef.current)) {
            setGenkaSummaryCell(map, "SSDGM_TSUKATANI", "total_value", "JPY");
            setGenkaSummaryCell(map, "SSDGM_RATE", "total_value", "1");
        }

        return normalizeGenkaSummaryRows(genkaSummaryOrder.map((k) => map[k] || emptyGenkaSummaryRow(k, "0")));
    }

    // 原価再計算オプションの予約口。
    function buildGenkaRecalcOptions(options = {}) {
        return {};
    }

    function buildGenkaBaseRowsForRecalc(panelInfo, calcOptions) {
        const current = GridData(panelInfo);
        const normalizedBaseRows = normalizeGenkaRowsByPattern(
            normalizeGenkaRowsWithSkeleton(current, entrydataRef.current)
        );
        return normalizedBaseRows;
    }

    function applyGenkaRowsToGrid(panelInfo, rows) {
        const nextRows = blankDefaultZeroGenkaRows(rows);
        SetGridData(panelInfo, nextRows);
        applyGridRowsNow(panelInfo, nextRows);
    }

    function buildGenkaSummaryPanels(panelInfo) {
        const panelId = String(DicVal(panelInfo, "id", "19"));
        const resolvedSummaryType = normalizeTotalTypeForColumns(PANEL_TYPE.GENKA_SUMMARY, "genka");
        const summaryPanels = [{ id: panelId, totaltype: resolvedSummaryType }];
        if (String(resolvedSummaryType ?? "") !== String(PANEL_TYPE.GENKA_SUMMARY ?? "")) {
            summaryPanels.push({ id: panelId, totaltype: PANEL_TYPE.GENKA_SUMMARY });
        }
        return summaryPanels;
    }

    function applyGenkaSummaryRowsToPanels(summaryPanels, summaryRows) {
        (summaryPanels || []).forEach((p) => {
            const normalizedSummaryRows = blankDefaultZeroGenkaSummaryRows(normalizeGenkaSummaryRows(DeepCopy(summaryRows)));
            ensureTotalDataRef(p).current = normalizedSummaryRows;
            applyTotalRowsNow(p, normalizedSummaryRows);
        });
    }

    function shouldSkipGlobalEntryRerender(options = {}) {
        return options.skipGlobalRerender === true || entryLoadingRef.current;
    }

    function finalizeGenkaRecalc(options = {}) {
        applyGenkaSummaryRowsToEntryData(entrydataRef.current);
        if (!shouldSkipGlobalEntryRerender(options)) {
            rerender();
        }
    }

    // 原価明細を再計算し、サマリとentrydataへ反映する。
    function recalcGenkaGridAndSummary(panelInfo, options = {}) {
        if (!isGenkaGridPanelInfo(panelInfo)) return;
        const calcOptions = buildGenkaRecalcOptions(options);
        const baseRows = buildGenkaBaseRowsForRecalc(panelInfo, calcOptions);

        // 原価ソースが空なら、左明細・右サマリとも表示用の空状態へ戻す。
        if (!hasActualGenkaRecalcSource(baseRows)) {
            const emptyRows = normalizeGenkaRowsByLayoutPrecision(normalizeGenkaRowsByPattern(buildGenkaSkeletonRows()));
            applyGenkaRowsToGrid(panelInfo, emptyRows);
            applyGenkaSummaryRowsToPanels(buildGenkaSummaryPanels(panelInfo), defaultGenkaSummaryRows());
            if (!shouldSkipGlobalEntryRerender(options)) {
                rerender();
            }
            return;
        }

        const patternNo = genkaParsePatternNoForCalc();
        if (!Number.isFinite(Number(patternNo)) || Number(patternNo) < 1 || Number(patternNo) > 9) {
            const displayRows = buildGenkaRowsForDisplay(entrydataRef.current);
            applyGenkaRowsToGrid(panelInfo, displayRows);
            const summaryRows = buildGenkaSummaryRowsFromFlatData(entrydataRef.current);
            applyGenkaSummaryRowsToPanels(
                buildGenkaSummaryPanels(panelInfo),
                Array.isArray(summaryRows) && summaryRows.length !== 0 ? summaryRows : defaultGenkaSummaryRows()
            );
            if (!shouldSkipGlobalEntryRerender(options)) {
                rerender();
            }
            return;
        }

        const calc = calcGenkaRowsByCellFormula(baseRows, calcOptions);
        applyGenkaRowsToGrid(panelInfo, calc.rows);
        const summaryRows = calcGenkaSummaryRowsByCellFormula(
            calc.rows,
            calc.patternNo,
            calc.expenseSlot,
            calcOptions
        );
        applyGenkaSummaryRowsToPanels(buildGenkaSummaryPanels(panelInfo), summaryRows);
        finalizeGenkaRecalc(options);
    }

    return {
        hasActualEntryValueByColumnId,
        normalizeGenkaRowsByPattern,
        hasActualGenkaEntryValues,
        hasActualGenkaEntryValueForCell,
        hasActualGenkaGridValues,
        hasActualGenkaSummaryValues,
        currentGenkaSummaryRowsForActualCheck,
        recalcGenkaGridAndSummary
    };
}
